import { useState, useRef, useEffect, type CSSProperties } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Sun, Moon, Monitor } from 'lucide-react'
import { QuickSearch } from './QuickSearch'
import { Cloche } from './Cloche'
import { useLienClient } from '../../contexts/ClientAfficheContext'
import { useAlertBadges } from '../../contexts/AlertBadgeContext'
import { useEcranLarge } from '../../hooks/useLargeurEcran'
import { useMsal } from '@azure/msal-react'
import { useProfile } from '../../hooks/useProfile'
import { useTenants } from '../../hooks/useTenants'
import { getNavStructure, itemCouvre } from '../../lib/navigation'
import { useSolutionActionsBandeau } from '../../lib/solutionRegistry'
import { DateDernierAudit } from './DateDernierAudit'
import { selectableAccounts } from '../../lib/accountScope'
import { useTheme, type Theme } from '../../hooks/useTheme'
import { Button } from '@/platform/components/ui/button'
import { roleLabel, roleLabelCourt } from '@/platform/lib/roleLabel'
import { VERSION_INFO } from '@/platform/lib/version'
import { formatDate } from '@/platform/lib/format'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/platform/components/ui/dropdown-menu'

interface HeaderProps {
  selectedAccountId: string | null
  onSelectTenant: (id: string) => void
  clientViewMode: boolean
  onToggleClientView: (v: boolean) => void
}

// Spec 159 — format de date unique (`platform/lib/format.ts`).
const fmtDate = formatDate

// Une ligne qui se tronque par « … » quand la barre manque de place ; le texte complet reste en infobulle.
const ELLIPSIS: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }

function ThemeSwitcher() {
  const { theme, effectiveTheme, setTheme, saving } = useTheme()

  // Icône affichée dans le bouton — reflète le thème effectif actuel,
  // sauf en mode 'auto' où on garde l'icône Monitor pour signaler "suivre l'OS".
  const Icon = theme === 'auto' ? Monitor : effectiveTheme === 'dark' ? Moon : Sun

  const options: { value: Theme; label: string; Ico: typeof Sun }[] = [
    { value: 'auto', label: 'Auto (système)', Ico: Monitor },
    { value: 'light', label: 'Clair', Ico: Sun },
    { value: 'dark', label: 'Sombre', Ico: Moon },
  ]

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="flex-shrink-0 h-auto p-1.5 text-foreground"
          title="Apparence"
          aria-label="Choisir le thème"
          disabled={saving}
        >
          <Icon size={15} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {options.map(opt => {
          const active = theme === opt.value
          return (
            <DropdownMenuItem
              key={opt.value}
              onSelect={() => { if (!active) void setTheme(opt.value) }}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <opt.Ico size={14} />
              <span style={{ flex: 1 }}>{opt.label}</span>
              {active && <span style={{ color: 'hsl(var(--accent))', fontWeight: 'bold' }}>●</span>}
            </DropdownMenuItem>
          )
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function getInitials(name: string): string {
  const local = name.replace(/@.*/, '').replace(/\+.*/, '')
  const parts = local.split(/[\s.]+/).filter(Boolean)
  if (parts.length >= 2) {
    return ((parts[0]![0] ?? '') + (parts[parts.length - 1]![0] ?? '')).toUpperCase()
  }
  return local.slice(0, 2).toUpperCase()
}

function formatDisplayName(name: string): string {
  if (!name.includes('@')) return name
  const local = name.replace(/@.*/, '').replace(/\+.*/, '')
  const parts = local.split(/[.\-_]+/).filter(Boolean)
  if (parts.length >= 2) {
    const first = parts[0]![0]!.toUpperCase() + '.'
    const last = parts[1]![0]!.toUpperCase() + parts[1]!.slice(1)
    return `${first} ${last}`
  }
  return local
}

export function Header({ selectedAccountId, onSelectTenant, clientViewMode, onToggleClientView }: HeaderProps) {
  const { instance } = useMsal()
  const { profile } = useProfile()
  const { accounts } = useTenants()
  const navigate = useNavigate()
  const lien = useLienClient()
  const { activeCount } = useAlertBadges()
  const ecranLarge = useEcranLarge()
  const [showProfileMenu, setShowProfileMenu] = useState(false)
  const profileMenuRef = useRef<HTMLDivElement>(null)

  const location = useLocation()
  const navStructure = profile ? getNavStructure(profile.role) : null
  // Spec 161 (garde-fou B2) — une entrée couvre aussi les pages de ses onglets : sans cela, une page regroupée sous un
  // onglet perdait la date d'audit de la barre du haut.
  const currentSection = navStructure?.sections.find(
    s => s.items.some(item => itemCouvre(item, location.pathname))
  ) ?? null
  // Spec 161, second retour de Christophe (2026-10-12) — la date s'affiche aussi sur l'accueil (Tableau de bord, Alertes),
  // qui ne la répète plus dans la page. Elle ne s'affiche toujours pas sur les pages Clients, Administration, Paramètres.
  const showAuditBanner = currentSection?.solutionCode === 'finops-m365' || currentSection?.id === 'accueil'
  const canSwitchTenant = profile?.role !== 'Client'

  // profile.accounts est garanti non-null (AuthGuard) — pas de dépendance async pour le nom.
  // Issue #47 — le sélecteur doit proposer EXACTEMENT ce que l'autorisation accepte : pour un
  // `Reseller`, `profile.accounts` porte en tête son propre compte, hors périmètre effectif.
  const profileTenants = selectableAccounts(profile)
  // Spec 160 (FR-002) — le bandeau ne calcule plus son propre client : il lit celui que la coquille a résolu, le même
  // que la page. Avant, un repli sur le premier compte pouvait faire viser un autre client à « Demander un audit ».
  // `null` quand l'adresse est refusée : le sélecteur n'affiche alors aucun client.
  const selectedProfileTenant = profileTenants.find(t => t.accountId === selectedAccountId) ?? null
  const selectedSummary = accounts.find(t => t.accountId === selectedAccountId) ?? null
  const showSelector = canSwitchTenant && profileTenants.length > 1
  // Périmètre vide (revendeur sans aucun client rattaché) : état explicite plutôt qu'un sélecteur
  // vide silencieux ou un 403.
  const emptyScope = profileTenants.length === 0
  // Spec 161, second retour (2026-10-12) — actions d'audit déclarées par les solutions, pour le client AFFICHÉ (celui de
  // la coquille). Jamais pour le client, ni en Vue client, ni hors des pages qui montrent la date (specs 084, 160).
  const peutAgir = profile?.role === 'Admin' || profile?.role === 'Consultant' || profile?.role === 'Reseller'
  const actionsAudit = useSolutionActionsBandeau({
    accountId: selectedAccountId,
    nomClient: selectedProfileTenant?.displayName ?? '',
    enabled: peutAgir && !clientViewMode && showAuditBanner && !emptyScope && selectedProfileTenant !== null,
  })

  const initials = profile ? getInitials(profile.displayName) : '?'
  const displayName = profile ? formatDisplayName(profile.displayName) : ''

  const handleLogout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: '/' }).catch(console.error)
  }

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setShowProfileMenu(false)
      }
    }
    if (showProfileMenu) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showProfileMenu])

  return (
    <header style={{
      display: 'flex',
      alignItems: 'center',
      height: 56,
      padding: '0 16px',
      background: 'hsl(var(--card))',
      borderBottom: '1px solid hsl(var(--border))',
      gap: 10,
      flexShrink: 0,
      zIndex: 10,
      minWidth: 0,
    }}>

      {/* Client selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flexShrink: 0, color: 'hsl(var(--foreground))' }}>
        <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <circle cx="8" cy="5.5" r="3"/>
          <path d="M2 15c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5"/>
        </svg>
        {showSelector ? (
          // Spec 160 (FR-010, FR-014) — un nom, et un focus visible : `outline: 'none'` le retirait.
          <select
            aria-label="Client affiché"
            value={selectedAccountId ?? ''}
            onChange={e => { if (e.target.value) onSelectTenant(e.target.value) }}
            className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--fs-body)',
              color: 'hsl(var(--primary))',
              fontWeight: 600,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              maxWidth: 200,
            }}
          >
            {/* Spec 160 — sans client résolu (adresse refusée), une option vide : sinon le navigateur afficherait le
                premier client de la liste, que la page ne montre pas. */}
            {!selectedAccountId && <option value="" disabled>Choisir un client</option>}
            {profileTenants.map(t => (
              <option key={t.accountId} value={t.accountId}>{t.displayName}</option>
            ))}
          </select>
        ) : (
          <span style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-body)',
            maxWidth: 200,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontStyle: emptyScope ? 'italic' : 'normal',
            fontWeight: emptyScope ? 400 : 600,
            color: emptyScope ? 'hsl(var(--muted-foreground))' : 'hsl(var(--primary))',
          }}>
            {emptyScope ? 'Aucun client dans votre portefeuille' : (selectedProfileTenant?.displayName ?? '—')}
          </span>
        )}
      </div>

      {/* Divider + date du dernier audit — pages de la solution et accueil (Tableau de bord, Alertes) ; rien pour un client
          refusé (spec 160). Spec 161, second retour : en menu des actions d'audit pour les profils qui les ont. */}
      {showAuditBanner && !emptyScope && selectedAccountId && (
        <>
          <div style={{ width: 1, height: 22, background: 'hsl(var(--border))', flexShrink: 0 }} />
          <DateDernierAudit date={fmtDate(selectedSummary?.lastSnapshotDate ?? null)} ecranLarge={ecranLarge} actions={actionsAudit.items} />
        </>
      )}
      {/* Ce que les actions ouvrent (confirmation, volet de suivi, échec) : en portail, hors de la barre de 56 px. */}
      {actionsAudit.element}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Search */}
      <QuickSearch selectedAccountId={selectedAccountId} />

      {/* Prévisualisation client */}
      {profile?.role !== 'Client' && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onToggleClientView(!clientViewMode)}
          title={clientViewMode ? 'Désactiver la prévisualisation client' : 'Activer la prévisualisation client'}
          className={clientViewMode ? 'border-accent text-accent bg-muted hover:bg-muted hover:text-accent' : ''}
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-caption)',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            flexShrink: 0,
            transition: 'background 0.12s, border-color 0.12s, color 0.12s',
          }}
        >
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M1 8C1 8 3.5 3 8 3s7 5 7 5-2.5 5-7 5S1 8 1 8z"/>
            <circle cx="8" cy="8" r="2"/>
          </svg>
          {clientViewMode ? 'Vue client active' : 'Vue client'}
        </Button>
      )}

      {/* Theme switcher — bascule rapide Clair / Sombre / Auto */}
      <ThemeSwitcher />

      {/* Help — spec 128 : ouvre le centre d'aide /aide */}
      <Button
        variant="ghost"
        className="flex-shrink-0 h-auto p-1.5 text-foreground"
        aria-label="Aide"
        title="Aide"
        onClick={() => navigate('/aide')}
      >
        <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="8" cy="8" r="6.5"/>
          <path d="M6 6.2C6 5 6.9 4 8 4s2 .9 2 2c0 1.3-1.2 1.8-1.8 2.3C7.6 8.8 7.5 9.3 7.5 9.8"/>
          <circle cx="7.5" cy="11.5" r=".75" fill="currentColor" stroke="none"/>
        </svg>
      </Button>

      {/* Notifications — spec 160 : le nombre d'alertes actives du client affiché, vers la page Alertes (FR-015) */}
      {selectedAccountId && <Cloche nombre={activeCount} href={lien('/alertes')} />}

      {/* Profile — avec sous-menu. Spec 159 : dernier élément de la barre, il se tronque (nom et rôle en infobulle)
          plutôt que de pousser sa flèche hors de l'écran — le libellé « Administrateur » la faisait sortir de 9 px à
          1 366 px en Vue client (décision du 2026-10-11). Facteur quasi nul : la recherche cède d'abord jusqu'à sa
          largeur minimale, le profil ne prend que le reste (à 1, il se tronquait à 3 lettres pour 9 px manquants). Cela tient
          parce que l'intercalaire `flex: 1` reste non gelé : la somme des facteurs vaut alors au moins 1, et le manque
          entier se répartit (CSS Flexbox § 9.7, étape 4b) — le retirer rendrait le profil presque incompressible.
          Pourquoi 0,0001 et pas 0,01 : AVANT que la recherche n'atteigne sa largeur minimale, le profil prend quand même
          sa part au prorata ; à 0,01 elle valait moins d'un pixel, mais assez pour déclencher l'ellipse — le nom du
          revendeur se tronquait sans manque de place (relevé du 2026-10-11). À 0,0001, cette part reste sous l'unité de
          mise en page de Chromium (1/64 px). */}
      <div ref={profileMenuRef} style={{ position: 'relative', flexShrink: 0.0001, minWidth: 0 }}>
        <button
          type="button"
          // Un bouton qui déplie, pas un menu au sens ARIA (ni rôle menu, ni flèches) : `aria-expanded` seul.
          aria-expanded={showProfileMenu}
          onClick={() => setShowProfileMenu(v => !v)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 7,
            maxWidth: '100%',
            minWidth: 0,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 6px',
            borderRadius: 3,
          }}
        >
          <div style={{
            width: 27,
            height: 27,
            borderRadius: '50%',
            background: 'hsl(var(--primary))',
            color: 'hsl(var(--primary-foreground))',
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-caption)',
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            letterSpacing: '0.03em',
          }}>
            {initials}
          </div>
          {profile && (
            <div style={{ textAlign: 'left', lineHeight: 1, minWidth: 0 }}>
              <div title={displayName} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-small)', color: 'hsl(var(--primary))', fontWeight: 600, marginBottom: 2, ...ELLIPSIS }}>
                {displayName}
              </div>
              <div title={roleLabel(profile.role)} style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-caption)', color: 'hsl(var(--foreground))', ...ELLIPSIS }}>
                {roleLabelCourt(profile.role)}
              </div>
            </div>
          )}
          <svg width="9" height="9" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.4, flexShrink: 0, color: 'hsl(var(--foreground))' }}>
            <path d="M2 4l4 4 4-4"/>
          </svg>
        </button>

        {showProfileMenu && (
          <div style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            background: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(0,0,0,0.10)',
            minWidth: 160,
            zIndex: 100,
          }}>
            <Button
              variant="ghost"
              onClick={() => { navigate('/settings?tab=profil'); setShowProfileMenu(false) }}
              className="w-full justify-start rounded-none h-auto px-3.5 py-2 text-xs font-normal text-foreground border-b border-muted"
            >
              Profil
            </Button>
            <Button
              variant="ghost"
              onClick={handleLogout}
              className="w-full justify-start rounded-none h-auto px-3.5 py-2 text-xs font-normal text-destructive hover:text-destructive"
            >
              Se déconnecter
            </Button>
            {/* Spec 093 — version exécutée, visible de tous les profils ; déplacée ici depuis le pied du menu par la
                spec 159 (même texte, même infobulle). Sert à vérifier quelle version tourne. */}
            <div
              title={`commit ${VERSION_INFO.gitSha} · build ${VERSION_INFO.buildTime} · ${VERSION_INFO.environment}`}
              style={{
                padding: '6px 14px 8px',
                borderTop: '1px solid hsl(var(--muted))',
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--fs-caption)',
                color: 'hsl(var(--foreground-secondary))',
                whiteSpace: 'nowrap',
              }}
            >
              Version v{VERSION_INFO.version} · {VERSION_INFO.gitSha}
            </div>
          </div>
        )}
      </div>
    </header>
  )
}
